import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import LayoutDashboard from '../../../components/layout/layout-dashboard';
import {
  Plus,
  Search,
  MoreVertical,
  Pencil,
  Copy,
  Archive,
  Trash2,
  Star,
  MapPin,
  Eye,
  EyeOff,
} from 'lucide-react';
import { useAuth } from '../../../AuthProvider';
import { supabase } from '../../../lib/supabase';
import {
  getAllJobOffers,
  archiveJobOffer,
  deleteJobOffer,
  duplicateJobOffer,
} from '../../../lib/offres';
import type { JobOffer, JobOfferStatus } from '../../../types/offres';
import { CONTRACT_TYPE_LABELS, STATUS_LABELS } from '../../../types/offres';
import toast from 'react-hot-toast';

const STATUS_STYLES: Record<JobOfferStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  published: 'bg-emerald-50 text-emerald-700',
  archived: 'bg-orange-50 text-orange-600',
};

export default function OffresListAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState<JobOffer[]>([]);
  const [filtered, setFiltered] = useState<JobOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobOfferStatus | 'all'>('all');
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  useEffect(() => {
    async function init() {
      if (!user) { navigate('/'); return; }

      const { data } = await supabase.from('users').select('role').eq('id', user.id).maybeSingle();
      const role = data?.role ?? null;
      if (role !== 'admin' && role !== 'editor') { navigate('/'); return; }
      setUserRole(role);

      await load();
    }
    init();
  }, [user?.id]);

  async function load() {
    try {
      setLoading(true);
      const data = await getAllJobOffers();
      setOffers(data);
    } catch {
      toast.error('Impossible de charger les offres.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let list = offers;
    if (statusFilter !== 'all') list = list.filter((o) => o.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (o) =>
          o.title.toLowerCase().includes(q) ||
          o.location_city.toLowerCase().includes(q) ||
          o.slug.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [offers, statusFilter, search]);

  async function handleArchive(id: string) {
    if (!confirm('Archiver cette offre ?')) return;
    try {
      await archiveJobOffer(id);
      toast.success('Offre archivée.');
      await load();
    } catch {
      toast.error('Impossible d\'archiver.');
    }
    setOpenMenu(null);
  }

  async function handleDelete(id: string) {
    if (!confirm('Supprimer définitivement cette offre ? Cette action est irréversible.')) return;
    try {
      await deleteJobOffer(id);
      toast.success('Offre supprimée.');
      await load();
    } catch {
      toast.error('Impossible de supprimer.');
    }
    setOpenMenu(null);
  }

  async function handleDuplicate(id: string) {
    try {
      const copy = await duplicateJobOffer(id);
      toast.success('Offre dupliquée en brouillon.');
      navigate(`/admin/offres/${copy.id}/edit`);
    } catch {
      toast.error('Impossible de dupliquer.');
    }
    setOpenMenu(null);
  }

  const counts = {
    all: offers.length,
    published: offers.filter((o) => o.status === 'published').length,
    draft: offers.filter((o) => o.status === 'draft').length,
    archived: offers.filter((o) => o.status === 'archived').length,
  };

  return (
    <LayoutDashboard>
      <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
      <div className="mx-auto max-w-7xl px-6 py-10">

        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Offres d'emploi</h1>
            <p className="mt-1 text-sm text-slate-500">Gérez les offres publiées sur recruteurs.io</p>
          </div>
          <Link
            to="/admin/offres/new"
            className="inline-flex items-center gap-2 rounded-xl bg-pink-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-pink-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nouvelle offre
          </Link>
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(['all', 'published', 'draft', 'archived'] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-xl border p-4 text-left transition-all ${statusFilter === s ? 'border-slate-900 bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`}
            >
              <p className="text-2xl font-bold text-slate-900">{counts[s]}</p>
              <p className="mt-0.5 text-xs font-medium text-slate-500 capitalize">
                {s === 'all' ? 'Total' : STATUS_LABELS[s as JobOfferStatus]}
              </p>
            </button>
          ))}
        </div>

        {/* Search + filter */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par titre, ville ou slug..."
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm text-slate-900 placeholder-slate-400 outline-none focus:border-slate-900"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as JobOfferStatus | 'all')}
            className="rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-900"
          >
            <option value="all">Tous les statuts</option>
            <option value="published">Publiées</option>
            <option value="draft">Brouillons</option>
            <option value="archived">Archivées</option>
          </select>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-600 border-t-transparent" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-slate-500 text-sm">Aucune offre trouvée.</p>
              <Link to="/admin/offres/new" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-pink-600 hover:text-pink-700">
                <Plus className="h-4 w-4" /> Créer la première offre
              </Link>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 text-left">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Titre</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 sm:table-cell">Contrat</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 md:table-cell">Ville</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Statut</th>
                  <th className="hidden px-5 py-3.5 text-xs font-semibold uppercase tracking-wider text-slate-500 lg:table-cell">Date</th>
                  <th className="px-5 py-3.5" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((offer) => {
                  const date = offer.published_at ?? offer.created_at;
                  const dateStr = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(new Date(date));
                  return (
                    <tr key={offer.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-2">
                          {offer.featured && <Star className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-400" />}
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{offer.title}</p>
                            <div className="flex items-center gap-2 mt-0.5">
                              <p className="text-xs text-slate-400">{offer.slug}</p>
                              {offer.parcours_id && (
                                <span className="inline-flex items-center rounded-full bg-pink-50 px-2 py-0.5 text-xs font-medium text-pink-600 border border-pink-100">
                                  Parcours lié
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hidden px-5 py-4 text-sm text-slate-600 sm:table-cell">
                        {CONTRACT_TYPE_LABELS[offer.contract_type]}
                      </td>
                      <td className="hidden px-5 py-4 md:table-cell">
                        <span className="flex items-center gap-1 text-sm text-slate-600">
                          <MapPin className="h-3.5 w-3.5 text-slate-400" />
                          {offer.location_city}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[offer.status]}`}>
                          {STATUS_LABELS[offer.status]}
                        </span>
                      </td>
                      <td className="hidden px-5 py-4 text-sm text-slate-500 lg:table-cell">{dateStr}</td>
                      <td className="px-5 py-4">
                        <div className="relative flex justify-end">
                          <button
                            onClick={() => setOpenMenu(openMenu === offer.id ? null : offer.id)}
                            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openMenu === offer.id && (
                            <div className="absolute right-0 top-8 z-10 w-48 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg">
                              <Link
                                to={`/admin/offres/${offer.id}/edit`}
                                className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                                onClick={() => setOpenMenu(null)}
                              >
                                <Pencil className="h-4 w-4 text-slate-400" /> Modifier
                              </Link>
                              {offer.status === 'published' && (
                                <a
                                  href={`/offres/${offer.slug}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                                  onClick={() => setOpenMenu(null)}
                                >
                                  <Eye className="h-4 w-4 text-slate-400" /> Voir la page
                                </a>
                              )}
                              <button
                                onClick={() => handleDuplicate(offer.id)}
                                className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                              >
                                <Copy className="h-4 w-4 text-slate-400" /> Dupliquer
                              </button>
                              {offer.status !== 'archived' && (
                                <button
                                  onClick={() => handleArchive(offer.id)}
                                  className="flex w-full items-center gap-2.5 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                                >
                                  <EyeOff className="h-4 w-4 text-slate-400" /> Archiver
                                </button>
                              )}
                              {userRole === 'admin' && (
                                <button
                                  onClick={() => handleDelete(offer.id)}
                                  className="flex w-full items-center gap-2.5 border-t border-slate-100 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" /> Supprimer
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      {/* Click outside to close menu */}
      {openMenu && (
        <div className="fixed inset-0 z-0" onClick={() => setOpenMenu(null)} />
      )}
      </div>
    </LayoutDashboard>
  );
}
